import { z } from 'zod';
// ============================================================================
// Provider enum (BYO email integration per Decision #10)
// ============================================================================
export const EmailProviderSchema = z.enum([
    'microsoft', // Microsoft 365 / Outlook via Microsoft Graph API
    'google', // Google Workspace / Gmail via Gmail API
    'smtp', // Generic SMTP/IMAP (self-hosted Exchange, custom mail systems, app-passwords)
]);
export const EmailAccountStatusSchema = z.enum([
    'connected', // Active and verified
    'disconnected', // User-initiated disconnect
    'error', // Auth failed, refresh expired, or send keeps failing
    'pending', // OAuth flow initiated but not yet completed
]);
// ============================================================================
// Provider-specific secrets (encrypted at rest before storage)
// ============================================================================
export const MicrosoftSecretsSchema = z.object({
    provider: z.literal('microsoft'),
    access_token: z.string(),
    refresh_token: z.string(),
    expires_at: z.string().datetime(),
    /** Microsoft Graph user/object ID. */
    graph_user_id: z.string().optional(),
});
export const GoogleSecretsSchema = z.object({
    provider: z.literal('google'),
    access_token: z.string(),
    refresh_token: z.string(),
    expires_at: z.string().datetime(),
});
export const SmtpSecretsSchema = z.object({
    provider: z.literal('smtp'),
    host: z.string().min(1),
    port: z.number().int().positive(),
    secure: z.boolean(), // STARTTLS / TLS on connect
    username: z.string().min(1),
    password: z.string().min(1),
    /** Optional IMAP config for future inbound features (Phase 4+). */
    imap_host: z.string().optional(),
    imap_port: z.number().int().positive().optional(),
    imap_secure: z.boolean().optional(),
});
export const EmailAccountSecretsSchema = z.discriminatedUnion('provider', [
    MicrosoftSecretsSchema,
    GoogleSecretsSchema,
    SmtpSecretsSchema,
]);
// ============================================================================
// EmailAccount (the public-facing shape, secrets stripped)
// ============================================================================
export const EmailAccountSchema = z.object({
    id: z.string().regex(/^ema_[A-Za-z0-9_-]+$/),
    customer_id: z.string().regex(/^cust_[A-Za-z0-9_-]+$/),
    provider: EmailProviderSchema,
    display_name: z.string().min(1).max(120),
    sender_address: z.string().email(),
    status: EmailAccountStatusSchema,
    last_send_success_at: z.string().datetime().nullable(),
    last_send_error: z.string().nullable(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
// ============================================================================
// Create/update API shapes
// ============================================================================
/**
 * Creating an SMTP account is a single API call (creds in body). Microsoft and
 * Google require an OAuth flow — clients hit `/v1/email-accounts/oauth/{provider}/init`
 * to start the flow, and the callback creates the account record.
 */
export const SmtpAccountCreateSchema = z.object({
    display_name: z.string().min(1).max(120),
    sender_address: z.string().email(),
    smtp_host: z.string().min(1),
    smtp_port: z.number().int().positive().default(587),
    smtp_secure: z.boolean().default(true),
    smtp_username: z.string().min(1),
    smtp_password: z.string().min(1),
    imap_host: z.string().optional(),
    imap_port: z.number().int().positive().optional(),
    imap_secure: z.boolean().optional(),
});
// ============================================================================
// EmailMessage (provider-agnostic)
// ============================================================================
export const EmailAddressSchema = z.object({
    email: z.string().email(),
    name: z.string().optional(),
});
export const EmailMessageSchema = z
    .object({
    to: z.array(EmailAddressSchema).min(1),
    cc: z.array(EmailAddressSchema).optional(),
    bcc: z.array(EmailAddressSchema).optional(),
    reply_to: z.string().email().optional(),
    subject: z.string().min(1).max(998), // RFC 5322 line-length limit
    html: z.string().optional(),
    text: z.string().optional(),
    headers: z.record(z.string()).optional(),
})
    .refine((m) => m.html || m.text, {
    message: 'message must have at least one of: html, text',
});
export const SendResultSchema = z.object({
    message_id: z.string().nullable(),
    /** Provider-specific response, included for diagnostics. Not persisted. */
    provider_response: z.unknown().optional(),
});
//# sourceMappingURL=email.js.map